const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A debounced function that delays invoking the wrapped function until after `wait` milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * @param {function} func The function to debounce.
 * @param {number} wait The number of milliseconds to delay.
 * @returns {function & { cancel: () => void }} A new debounced function.
 */
function debounce(func, wait) {
  let timeout;

  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

/**
 * A custom element that displays a marquee.
 * @extends {HTMLElement}
 */
class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.animation = null;
  }

  get pauseWhenFits() {
    return this.hasAttribute('data-pause-when-fits');
  }

  connectedCallback() {
    this.wrapper = this.querySelector('[ref="wrapper"]');
    this.content = this.querySelector('[ref="content"]');

    if (!this.wrapper || !this.content) {
      console.error('Marquee component is missing wrapper or content element.');
      return;
    }
    this.updateMotionState();

    window.addEventListener('resize', this.handleResize);
    this.addEventListener('pointerenter', this.slowDown);
    this.addEventListener('pointerleave', this.speedUp);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('pointerenter', this.slowDown);
    this.removeEventListener('pointerleave', this.speedUp);
    this.slowDown.cancel();
    this.handleResize.cancel();
  }

  fitsInViewport() {
    const items = this.content?.firstElementChild;
    if (!(items instanceof HTMLElement)) return false;

    return items.offsetWidth <= this.offsetWidth;
  }

  updateMotionState() {
    const shouldPause = this.pauseWhenFits && this.fitsInViewport();

    if (shouldPause) {
      this.setAttribute('data-disabled', '');
      this.resetRepeatedItems();
      this.clonedContent?.remove();
      return;
    }

    this.removeAttribute('data-disabled');
    this.resetRepeatedItems();
    this.addRepeatedItems();
    this.duplicateContent();
    this.setSpeed();
  }

  resetRepeatedItems() {
    const wrapper = this.content?.firstElementChild;
    if (!wrapper) return;

    while (this.content.children.length > 1) {
      this.content.lastElementChild?.remove();
    }
  }

  slowDown = debounce(() => {
    if (this.animation) return;

    const animation = this.wrapper.getAnimations()[0];

    if (!animation) return;

    this.animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  speedUp = () => {
    this.slowDown.cancel();

    const animation = this.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.animation?.current ?? 0;
    this.animation?.cancel();

    this.animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.animation = null;
      },
    });
  };

  get clonedContent() {
    const lastChild = this.wrapper.lastElementChild;
    return this.content !== lastChild ? lastChild : null;
  }

  setSpeed(value = this.calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  handleResize = debounce(() => {
    if (this.pauseWhenFits) {
      this.updateMotionState();
      this.restartAnimation();
      return;
    }

    const newNumberOfCopies = this.calculateNumberOfCopies();
    const currentNumberOfCopies = this.content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.duplicateContent();
    this.setSpeed();
    this.restartAnimation();
  }, 250);

  restartAnimation() {
    const animations = this.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.wrapper.appendChild(clone);
  }

  addRepeatedItems(numberOfCopies = this.calculateNumberOfCopies()) {
    const wrapper = this.content.firstElementChild;
    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      this.content.appendChild(clone);
    }
  }

  removeRepeatedItems(numberOfCopies = this.calculateNumberOfCopies()) {
    for (let i = 0; i < numberOfCopies; i++) {
      this.content.lastElementChild?.remove();
    }
  }

  calculateNumberOfCopies() {
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      this.content.firstElementChild instanceof HTMLElement ? this.content.firstElementChild.offsetWidth : 0;
    return marqueeRepeatedItemWidth > 0 ? Math.ceil(marqueeWidth / marqueeRepeatedItemWidth) : 1;
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {(value: number) => void} params.onUpdate - The function to call on each update.
 * @param {(t: number) => number} [params.easing] - The easing function.
 * @param {() => void} [params.onComplete] - The function to call when the animation completes.
 * @returns {{current: number, cancel: () => void}}
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
